import { htmlAttr, styleProp } from './attrs.js';

// Append <head> block HTML to document.head. Scripts are recreated so they run.
function appendHeadHtml(html) {
  const container = document.createElement('div');
  container.innerHTML = html;
  for (const node of Array.from(container.children)) {
    if (node.tagName !== 'SCRIPT') {
      document.head.appendChild(node);
      continue;
    }
    const script = document.createElement('script');
    for (const attr of node.attributes) {
      script.setAttribute(attr.name, attr.value);
    }
    script.type ||= 'text/javascript';
    if (node.src) {
      document.head.appendChild(script);
    } else if (script.type.includes('javascript') || script.type === 'module') {
      script.textContent = node.textContent;
      document.head.appendChild(script);
    }
  }
}

// Utility functions that extend Fez
export default (Fez) => {
  // Script from URL
  //   Fez.head({ js: 'https://example.com/script.js' });
  // Script with attributes
  //   Fez.head({ js: 'https://example.com/script.js', type: 'module', async: true });
  // Script with callback
  //   Fez.head({ js: 'https://example.com/script.js' }, () => { console.log('loaded') });
  // Module loading with auto-import to window
  //   Fez.head({ js: 'https://example.com/module.js', module: 'MyModule' }); // imports and sets window.MyModule
  // CSS inclusion
  //   Fez.head({ css: 'https://example.com/styles.css' });
  // CSS with additional attributes and callback
  //   Fez.head({ css: 'https://example.com/styles.css', media: 'print' }, () => { console.log('CSS loaded') })
  // Inline script evaluation
  //   Fez.head({ script: 'console.log("Hello world")' })
  // Fez component loading
  //   Fez.head({ fez: 'path/to/component.fez' })
  // Extract from nodes
  //   Fez.head(domNode)
  Fez.head = (config, callback) => {
    // a component's <head> block: its HTML, appended to document.head
    if (typeof config === 'string') {
      appendHeadHtml(config);
      return;
    }

    if (config.nodeName) {
      if (config.nodeName === 'SCRIPT') {
        Fez.head({ script: config.innerText });
        config.remove();
      } else {
        config.querySelectorAll('script').forEach((n) => Fez.head(n));
        config
          .querySelectorAll('template[fez], xmp[fez], script[fez]')
          .forEach((n) => Fez.compile(n));
      }

      return;
    }

    if (typeof config !== 'object' || config === null) {
      throw new Error('head requires an object parameter');
    }

    let src, elementType;
    const attributes = {};

    // Load Fez component(s) from URL
    // Supports:
    //   - Single component: { fez: 'path/to/component.fez' }
    //   - Component list:   { fez: 'path/to/components.txt' }
    //     txt file contains one component path per line (relative to txt location or absolute if starts with /)
    if (config.fez) {
      const fezPath = config.fez;

      // If it's a txt file, load it as a component list
      if (fezPath.endsWith('.txt')) {
        Fez.fetch(fezPath)
          .then((content) => {
            // Get base path from txt file location
            const basePath = fezPath.substring(0, fezPath.lastIndexOf('/') + 1);

            // Parse lines, filter empty lines and comments
            const lines = content
              .split('\n')
              .map((line) => line.trim())
              .filter((line) => line && !line.startsWith('#'));

            // Load each component; one failure must not stop the others nor
            // leave the callback waiting forever.
            const loads = lines.map((line) => {
              let componentPath;
              if (line.startsWith('/')) {
                componentPath = line;
              } else {
                const path = line.endsWith('.fez') ? line : line + '.fez';
                componentPath = basePath + path;
              }

              const name = Fez.nameFromPath(componentPath);
              return Fez.fetch(componentPath)
                .then((componentContent) => Fez.compile(name, componentContent))
                .catch((error) =>
                  Fez.onError('compile', `Load error for "${componentPath}": ${error.message}`),
                );
            });

            Promise.all(loads).then(() => {
              if (callback) {
                callback();
              }
            });
          })
          .catch((error) => {
            Fez.onError('compile', `Load error for "${fezPath}": ${error.message}`);
            if (callback) {
              callback(error);
            }
          });
        return;
      }

      // Single .fez component
      const name = Fez.nameFromPath(fezPath);
      Fez.fetch(fezPath)
        .then((content) => {
          Fez.compile(name, content);
          if (callback) {
            callback();
          }
        })
        .catch((error) => {
          Fez.onError('compile', `Load error for "${fezPath}": ${error.message}`);
          if (callback) {
            callback(error);
          }
        });
      return;
    }

    if (config.script) {
      if (config.script.includes('import ')) {
        // Inline module scripts never fire load/error, so evaluate the source
        // through a blob URL and let the dynamic import resolve after the
        // module's import graph has run.
        const blobUrl = URL.createObjectURL(new Blob([config.script], { type: 'text/javascript' }));
        import(/* webpackIgnore: true */ blobUrl)
          .then(() => {
            URL.revokeObjectURL(blobUrl);
            if (callback) {
              callback(null);
            }
          })
          .catch((error) => {
            URL.revokeObjectURL(blobUrl);
            if (callback) {
              callback(error);
            }
          });
      } else {
        try {
          new Function(config.script)();
          if (callback) {
            callback();
          }
        } catch (error) {
          Fez.consoleError('Error executing script:', error);
          console.log(config.script);
        }
      }
      return;
    } else if (config.js) {
      src = config.js;
      elementType = 'script';
      // Copy all properties except 'js' as attributes
      for (const [key, value] of Object.entries(config)) {
        if (key !== 'js' && key !== 'module') {
          attributes[key] = value;
        }
      }
      // Handle module loading
      if (config.module) {
        attributes.type = 'module';
      }
    } else if (config.css) {
      src = config.css;
      elementType = 'link';
      attributes.rel = 'stylesheet';
      // Copy all properties except 'css' as attributes
      for (const [key, value] of Object.entries(config)) {
        if (key !== 'css') {
          attributes[key] = value;
        }
      }
    } else {
      throw new Error('head requires either "script", "js" or "css" property');
    }

    // Import the module's default/namespace onto window[config.module]. Runs on
    // a fresh load and on a cache hit (the tag may already be loaded, but the
    // window binding would otherwise never be set).
    const assignModule = () => {
      if (config.module && elementType === 'script') {
        import(src)
          .then((module) => {
            window[config.module] = module.default || module[config.module] || module;
          })
          .catch((error) => {
            console.error(`Error importing module ${config.module}:`, error);
          });
      }
    };

    // Match by attribute value instead of interpolating src into a selector -
    // a URL containing a quote would otherwise throw inside querySelector.
    const attrName = elementType === 'link' ? 'href' : 'src';
    const existingNode = Array.from(document.querySelectorAll(elementType)).find(
      (n) => n.getAttribute(attrName) === src || n[attrName] === src,
    );
    if (existingNode) {
      assignModule();
      if (callback) {
        callback();
      }
      return existingNode;
    }

    const element = document.createElement(elementType);

    if (elementType === 'link') {
      element.href = src;
    } else {
      element.src = src;
    }

    for (const [key, value] of Object.entries(attributes)) {
      element.setAttribute(key, value);
    }

    if (callback || config.module) {
      element.onload = () => {
        assignModule();
        if (callback) {
          callback();
        }
      };
    }

    document.head.appendChild(element);

    return element;
  };

  // Cache configuration
  const FETCH_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  const FETCH_CACHE_MAX_SIZE = 100;

  // Fetch wrapper with automatic caching and data handling
  // Usage:
  //   Fez.fetch(url) - GET request (default)
  //   Fez.fetch(url, callback) - GET with callback
  //   Fez.fetch(url, data) - GET with query params (?foo=bar&baz=qux)
  //   Fez.fetch(url, data, callback) - GET with query params and callback
  //   Fez.fetch('POST', url, data) - POST with FormData body (multipart/form-data)
  //   Fez.fetch('POST', url, data, callback) - POST with FormData and callback
  // Data object is automatically converted:
  //   - GET: appended as URL query parameters
  //   - POST: sent as FormData (multipart/form-data) without custom headers
  Fez.fetch = function (...args) {
    // Initialize cache if not exists
    Fez._fetchCache ||= new Map();

    let method = 'GET';
    let url;
    let callback;

    // Check if first arg is HTTP method (uppercase letters)
    if (typeof args[0] === 'string' && /^[A-Z]+$/.test(args[0])) {
      method = args.shift();
    }

    // URL is required
    url = args.shift();

    // Check for data/options object
    const opts = {};
    let data = null;
    if (typeof args[0] === 'object') {
      data = args.shift();
    }

    // Check for callback function
    if (typeof args[0] === 'function') {
      callback = args.shift();
    }

    // Handle data based on method
    if (data) {
      if (method === 'GET') {
        // For GET, append data as query parameters
        const params = new URLSearchParams(data);
        url += (url.includes('?') ? '&' : '?') + params.toString();
      } else if (method === 'POST') {
        // For POST, convert to FormData
        const formData = new FormData();
        for (const [key, value] of Object.entries(data)) {
          formData.append(key, value);
        }
        opts.body = formData;
      }
    }

    // Set method
    opts.method = method;

    // mark as a programmatic request so the server's request.xhr? is reliable
    opts.headers = { 'x-requested-with': 'XMLHttpRequest', ...opts.headers };

    // Create cache key from method, url, and stringified opts
    const cacheKey = `${method}:${url}:${JSON.stringify(opts)}`;

    // Only GET is cacheable / de-dup-able. POST is not idempotent, and its body
    // (FormData) is not captured by cacheKey - JSON.stringify(opts) drops it -
    // so caching or sharing a POST would silently return a stale response and
    // swallow later submissions. POSTs must always hit the network.
    const isGet = method === 'GET';

    // Check cache first (with TTL validation)
    const cached = isGet ? Fez._fetchCache.get(cacheKey) : null;
    if (cached && Date.now() - cached.timestamp < FETCH_CACHE_TTL) {
      Fez.consoleLog(`fetch cache hit: ${method} ${url}`);
      if (callback) {
        callback(cached.data);
        return;
      }
      return Promise.resolve(cached.data);
    }

    // Helper to process and cache response. An error status rejects: a 404
    // page must never be cached or compiled as if it were the resource.
    const processResponse = (response) => {
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText} for ${method} ${url}`.trim());
      }
      if (response.headers.get('content-type')?.includes('application/json')) {
        return response.json();
      }
      return response.text();
    };

    // Helper to store in cache with size limit
    const storeInCache = (key, data) => {
      // Enforce max cache size by removing oldest entries
      if (Fez._fetchCache.size >= FETCH_CACHE_MAX_SIZE) {
        const oldestKey = Fez._fetchCache.keys().next().value;
        Fez._fetchCache.delete(oldestKey);
      }
      Fez._fetchCache.set(key, { data, timestamp: Date.now() });
    };

    // De-dup concurrent requests. The resolved-value cache above only helps once
    // a request has completed; N callers asking for the same URL in the same tick
    // (e.g. multiple instances of a component fetching its .fez) all miss it and
    // each fire their own network request. Share a single in-flight promise keyed
    // by cacheKey instead, so they collapse onto one fetch + parse. (Each joined
    // caller still runs its own .then continuation, so this does not de-dup
    // downstream work such as compilation - only the request itself.)
    Fez._fetchInflight ||= new Map();

    let request = isGet ? Fez._fetchInflight.get(cacheKey) : null;
    if (request) {
      Fez.consoleLog(`fetch inflight: ${method} ${url}`);
    } else {
      Fez.consoleLog(`fetch live: ${method} ${url}`);
      request = fetch(url, opts)
        .then(processResponse)
        .then((data) => {
          if (isGet) {
            storeInCache(cacheKey, data);
          }
          return data;
        });

      if (isGet) {
        // Drop the in-flight entry once settled (success OR failure) so a later
        // call re-fetches: after the value-cache TTL, or immediately if this
        // request failed and cached nothing. Without this the map grows unbounded
        // and a transient failure would poison the URL permanently.
        request = request.finally(() => Fez._fetchInflight.delete(cacheKey));
        Fez._fetchInflight.set(cacheKey, request);
      }
    }

    // If callback provided, execute and handle
    if (callback) {
      request.then((data) => callback(data)).catch((error) => Fez.onError('fetch', error));
      return;
    }

    // Return promise with automatic JSON parsing
    return request;
  };

  // Clear fetch cache (useful for testing or manual cache invalidation)
  Fez.clearFetchCache = () => {
    Fez._fetchCache?.clear();
    Fez._fetchInflight?.clear();
  };

  Fez.darkenColor = (color, percent = 20) => {
    // Convert hex to RGB
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = ((num >> 8) & 0x00ff) - amt;
    const B = (num & 0x0000ff) - amt;
    return (
      '#' +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  };

  Fez.lightenColor = (color, percent = 20) => {
    // Convert hex to RGB
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      '#' +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  };

  /**
   * Escapes HTML special characters of any value as text; null and
   * undefined render nothing
   */
  Fez.htmlEscape = (text) => {
    if (text == null) {
      return '';
    }
    return String(text)
      .replaceAll('&', '&amp;')
      .replaceAll("'", '&apos;')
      .replaceAll('"', '&quot;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');
  };

  // `name={expr}` in a template: the whole attribute, or '' to drop it
  Fez.htmlAttr = (name, value) => htmlAttr(name, value, Fez.htmlEscape);
  Fez.styleProp = styleProp;

  // Escape a value for a quoted JS string inside handler code, so a quote in
  // the data cannot end the string: onclick="fez.rm('{id}')"
  Fez.jsEscape = (value) => {
    if (value == null) {
      return '';
    }
    return JSON.stringify(String(value))
      .slice(1, -1)
      .replace(/['`$]/g, '\\$&');
  };

  // create dom root and return it
  Fez.domRoot = (data, name = 'div') => {
    if (data instanceof Node) {
      return data;
    } else {
      const root = document.createElement(name);
      root.innerHTML = data;
      return root;
    }
  };

  // add class by name to node and remove it from siblings
  Fez.activateNode = (node, klass = 'active') => {
    if (!node || !node.parentElement) {
      return;
    }
    Array.from(node.parentElement.children).forEach((child) => {
      child.classList.remove(klass);
    });
    node.classList.add(klass);
  };

  Fez.isTrue = (val) => {
    return ['1', 'true', 'on'].includes(String(val).toLowerCase());
  };

  // public utility - do not remove
  Fez.uid = (() => {
    let seq = 111;
    return () => 'fez_uid_' + (++seq).toString(32);
  })();

  // get global function pointer, used to pass functions to nested or inline elements
  // <some-node :callback="${Fez.pointer(opts.callback)}" ...>
  // Pointers are automatically cleaned up after first use (one-time use by default)
  // Use Fez.pointer(func, { persist: true }) to keep the pointer
  Fez.POINTER_SEQ = 0;
  Fez.POINTER = {};
  Fez.POINTER_CREATED = {};
  Fez.pointer = (func, opts = {}) => {
    if (typeof func === 'function') {
      const uid = ++Fez.POINTER_SEQ;

      if (opts.persist) {
        Fez.POINTER[uid] = func;
      } else {
        Fez.POINTER_CREATED[uid] = Date.now();
        Fez.POINTER[uid] = (...args) => {
          const result = func(...args);
          delete Fez.POINTER[uid];
          delete Fez.POINTER_CREATED[uid];
          return result;
        };
      }

      return `Fez.POINTER[${uid}]`;
    }
  };

  // Sweep stale one-time pointers older than 5 minutes
  Fez.sweepPointers = () => {
    const cutoff = Date.now() - 5 * 60 * 1000;
    for (const uid of Object.keys(Fez.POINTER_CREATED)) {
      if (Fez.POINTER_CREATED[uid] < cutoff) {
        delete Fez.POINTER[uid];
        delete Fez.POINTER_CREATED[uid];
      }
    }
  };
  if (typeof document !== 'undefined') {
    setInterval(Fez.sweepPointers, 60 * 1000);
  }

  // Resolve a function from a string or function reference
  Fez.getFunction = (pointer) => {
    if (!pointer) {
      return () => {};
    } else if (typeof pointer === 'function') {
      return pointer;
    } else if (typeof pointer === 'string') {
      // Check if it's a function expression (arrow function or function keyword)
      // Arrow function: (args) => or args =>
      const arrowFuncPattern = /^\s*(?:async\s*)?(?:\([^)]*\)|\w+)\s*=>/;
      const functionPattern = /^\s*function\s*\(/;

      if (arrowFuncPattern.test(pointer) || functionPattern.test(pointer)) {
        return new Function('return ' + pointer)();
      } else if (pointer.includes('.') && !pointer.includes('(')) {
        // It's a property access like "this.focus" - return a function that calls it
        return new Function(`return function() { return ${pointer}(); }`);
      } else {
        // It's a function body
        return new Function(pointer);
      }
    }
    // Truthy but not a function or string (object/array): nothing callable
    return () => {};
  };

  // Execute a function when DOM is ready, or immediately if already loaded or
  // if there is no DOM at all (Node/SSR import)
  Fez.onReady = (callback) => {
    if (typeof document === 'undefined') {
      callback();
    } else if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => {
          callback();
        },
        { once: true },
      );
    } else {
      callback();
    }
  };

  // get unique id from string (32-bit FNV-1; Math.imul keeps the multiply in
  // 32-bit range so the hash does not lose precision as a float)
  Fez.fnv1 = (str) => {
    let hash = 2166136261;
    for (let i = 0; i < str.length; i++) {
      hash ^= str.charCodeAt(i);
      hash = Math.imul(hash, 16777619) >>> 0;
    }
    return hash.toString(36);
  };

  // Component name from a URL/path: drop query/hash, directory and the final
  // extension. `my.component.fez` -> `my.component` (split('.')[0] would give
  // `my`).
  Fez.nameFromPath = (url) => {
    const clean = String(url).split(/[?#]/)[0];
    const base = clean.split('/').pop() || '';
    return base.replace(/\.[^.]*$/, '');
  };

  // execute function until it returns true
  Fez.untilTrue = (func, pingRate) => {
    pingRate ||= 200;

    if (!func()) {
      setTimeout(() => {
        Fez.untilTrue(func, pingRate);
      }, pingRate);
    }
  };

  // Default throttle delay in ms
  const DEFAULT_THROTTLE_DELAY = 200;

  // throttle function calls
  Fez.throttle = (func, delay = DEFAULT_THROTTLE_DELAY) => {
    let lastRun = 0;
    let timeout;

    return function (...args) {
      const now = Date.now();

      if (now - lastRun >= delay) {
        func.apply(this, args);
        lastRun = now;
      } else {
        clearTimeout(timeout);
        timeout = setTimeout(
          () => {
            func.apply(this, args);
            lastRun = Date.now();
          },
          delay - (now - lastRun),
        );
      }
    };
  };

  // Enhanced truthiness check for template conditionals (#if, #unless)
  // Empty arrays and empty objects are falsy, everything else uses standard JS truthiness
  Fez.isTruthy = (v) => {
    if (Array.isArray(v)) {
      return v.length > 0;
    }
    if (v && typeof v === 'object') {
      return Object.keys(v).length > 0;
    }
    return !!v;
  };

  // Convert any collection to pairs for loop destructuring
  // Array: ['a', 'b'] → [['a', 0], ['b', 1]] (value, index)
  // Object: {x: 1} → [['x', 1]] (key, value)
  Fez.toPairs = (c) => {
    if (Array.isArray(c)) {
      return c.map((v, i) => [v, i]);
    }
    if (c && typeof c === 'object') {
      return Object.entries(c);
    }
    return [];
  };

  // public utility - do not remove
  Fez.tag = (tag, opts = {}, html = '') => {
    const json = encodeURIComponent(JSON.stringify(opts));
    return `<${tag} data-props="${json}">${html}</${tag}>`;
  };

  // public utility - do not remove
  Fez.typeof = (data) => {
    if (data === null || data === undefined) {
      return 'u';
    }
    if (Array.isArray(data)) {
      return 'a';
    }
    const t = typeof data;
    if (t === 'function') {
      return 'f';
    }
    if (t === 'string') {
      return 's';
    }
    if (t === 'number') {
      return Number.isInteger(data) ? 'i' : 'n';
    }
    if (t === 'object') {
      return 'o';
    }
    return t[0];
  };
};
